import React, { useEffect, useRef } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { withRouter } from 'react-router'

import { fetchCruiseOffer } from 'actions/CruiseActions'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import CruiseContent from './content/CruiseContent'
import { getCruiseShareModalConfig } from 'selectors/cruiseOfferSelectors'
import { ShareModalConfig } from 'lib/customer/getShareModalConfig'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { cruiseOfferClicked } from 'analytics/eventDefinitions'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import offerPageURL from 'lib/offer/offerPageURL'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPageWalledGarden'

interface Props extends RouteComponentProps<{ offerId: string, offerSlug: string }> {
  fetching: boolean
  offerError?: any
  cruiseOffer?: App.CruiseOffer
  shareModalConfig: ShareModalConfig
  currentRegionCode: string
}

function CruiseOfferPage(props: Props) {
  const {
    fetching,
    match,
    offerError,
    cruiseOffer,
    shareModalConfig,
    currentRegionCode,
  } = props

  const dispatch = useAppDispatch()
  const dataLoaded = useRef(false)

  useEuropeOfferPageWalledGarden()

  useEffect(() => {
    if (!cruiseOffer || cruiseOffer?.id !== match.params.offerId) {
      dispatch(fetchCruiseOffer(match.params.offerId))
    }
  }, [cruiseOffer, dispatch, match.params.offerId])

  useEffect(() => {
    if (cruiseOffer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(cruiseOfferClicked(cruiseOffer))
      dataLoaded.current = true
    }
  }, [cruiseOffer])

  usePageActivityTracking()

  if (offerError) {
    if (offerError?.status == 404) {
      return <Error404PageWithOffers />
    } else {
      return <Error500Page />
    }
  }

  if (fetching || !cruiseOffer) {
    return <LoadingIndicator />
  }

  if (match.params.offerSlug) {
    return <RedirectWithStatus code={301} to={`/${currentRegionCode.toLowerCase()}${offerPageURL(cruiseOffer)}`} />
  }

  return <CruiseContent offer={cruiseOffer} shareModalConfig={shareModalConfig} />
}

function mapStateToProps(appState: App.State, ownProps: Partial<Props>) {
  const { offerId } = ownProps.match.params
  const cruiseOffer = appState.cruise.cruiseOffers[offerId]

  return {
    fetching: appState.cruise.cruiseOffersLoading[offerId],
    offerError: appState.cruise.cruiseOffersErrors[offerId],
    cruiseOffer,
    shareModalConfig: getCruiseShareModalConfig(appState, cruiseOffer),
    currentRegionCode: appState.geo.currentRegionCode,
  }
}

export default withRouter(connect(mapStateToProps)(CruiseOfferPage))
